import React from "react";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { ToggleIcon } from "~/common/components/icon/ToggleIcon";
import type { GroupedQuoteLines, RenderCboxFn } from "~/features/orders/common/components/v2/QuoteTable/types";
import { splitAlternatives } from "~/features/orders/common/components/v2/QuoteTable/utils";
import { QuoteGroup, LineNumberCell, QuoteCard, QuoteSection, RowDivider, ExpandToggle } from "./styled";
import { ProductHeader } from "./ProductHeader";
import { QuotePartsTable } from "./QuotePartsTable";

import { useAbsoluteLineAlignment } from "./useLineNoOffsetCalculation";

interface AdvancedQuoteRowProps extends GroupedQuoteLines {
  renderCheckbox: RenderCboxFn;
}

/**
 * AdvancedQuoteRow - Displays a quote row with optional alternatives.
 *
 * Implements line number alignment using absolute positioning to ensure cross-browser
 * compatibility, specifically addressing Safari issues with CSS Grid row subgrid.
 */
export const AdvancedQuoteRow = (props: AdvancedQuoteRowProps) => {
  const { mainQuoteLine, sameProductAlternatives, differentProductAlternatives } = splitAlternatives(props);
  const { val: isAlternativeExpanded, toggle: toggleAlternative } = useBooleanState(false);
  const { val: isAlternativeProductsExpanded, toggle: toggleAlternativeProducts } = useBooleanState(false);
  const renderCheckbox = props.renderCheckbox;

  const { containerRef, registerTarget, getLineNumberStyle } = useAbsoluteLineAlignment();

  const isAnyExpanded = isAlternativeExpanded || isAlternativeProductsExpanded;

  return (
    <QuoteGroup role="rowgroup" ref={containerRef}>
      {/* Main line number */}
      <LineNumberCell style={getLineNumberStyle(mainQuoteLine.lineNo)}>{mainQuoteLine.lineNo}</LineNumberCell>

      <QuoteCard sx={{ pb: 2 }}>
        <QuoteSection indicatorColor={isAnyExpanded ? "main" : undefined}>
          {/* Product Header */}
          <ProductHeader quoteLine={mainQuoteLine} includeQuoteLine={false} />

          {/* Main Quote Line Section */}
          <QuotePartsTable
            ref={registerTarget(mainQuoteLine.lineNo)}
            quoteLine={mainQuoteLine}
            forceRender
            renderCheckbox={renderCheckbox}
          />
        </QuoteSection>

        {/* Alternative Quote Lines (same product) */}
        {sameProductAlternatives.length > 0 && (
          <QuoteSection indicatorColor={isAlternativeExpanded ? "alternative" : undefined}>
            <ExpandToggle onClick={toggleAlternative}>
              <ToggleIcon isActive={isAlternativeExpanded} />
              {isAlternativeExpanded ? "Hide" : "Show"} alternative quote lines
            </ExpandToggle>
            {isAlternativeExpanded &&
              sameProductAlternatives.map((quoteLine) => (
                <QuotePartsTable
                  key={quoteLine.lineNo}
                  ref={registerTarget(quoteLine.lineNo)}
                  quoteLine={quoteLine}
                  variant="alternative"
                  renderCheckbox={renderCheckbox}
                />
              ))}
          </QuoteSection>
        )}

        {/* Alternative Products */}
        {differentProductAlternatives.length > 0 && (
          <QuoteSection indicatorColor={isAlternativeProductsExpanded ? "alternativeProduct" : undefined}>
            <ExpandToggle onClick={toggleAlternativeProducts}>
              <ToggleIcon isActive={isAlternativeProductsExpanded} />
              {isAlternativeProductsExpanded ? "Hide" : "Show"} alternative product(s)
            </ExpandToggle>
            {isAlternativeProductsExpanded &&
              differentProductAlternatives.map(({ quoteLines }) => (
                <React.Fragment key={quoteLines[0].lineNo}>
                  <RowDivider />
                  <ProductHeader
                    ref={registerTarget(quoteLines[0].lineNo)}
                    quoteLine={quoteLines[0]}
                    includeQuoteLine={false}
                    alternative
                  />
                  {quoteLines.map((quoteLine, index) => (
                    <QuotePartsTable
                      key={quoteLine.lineNo}
                      // Only register target if it's NOT the first line (first line aligned with ProductHeader)
                      ref={index > 0 ? registerTarget(quoteLine.lineNo) : undefined}
                      quoteLine={quoteLine}
                      variant="alternative"
                      renderCheckbox={renderCheckbox}
                    />
                  ))}
                </React.Fragment>
              ))}
          </QuoteSection>
        )}
      </QuoteCard>

      {/* Alternative line numbers - shown when expanded */}
      {isAlternativeExpanded &&
        sameProductAlternatives.map((quoteLine) => (
          <LineNumberCell key={quoteLine.lineNo} style={getLineNumberStyle(quoteLine.lineNo)}>
            {quoteLine.lineNo}
          </LineNumberCell>
        ))}
      {isAlternativeProductsExpanded &&
        differentProductAlternatives
          .flatMap(({ quoteLines }) => quoteLines)
          .map((quoteLine) => (
            <LineNumberCell key={quoteLine.lineNo} style={getLineNumberStyle(quoteLine.lineNo)}>
              {quoteLine.lineNo}
            </LineNumberCell>
          ))}
    </QuoteGroup>
  );
};
